import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { getDailyOffNeeded, getRemainingDailyOffDutyRequired, getTotalDailyOffDutyRequired, getTotalDailyRestRequired } from "../availabilityHelpers";
import { isMaskedByEmergencyResponseFloor } from "../exemptions/emergencyResponse";
import { getTimeLeftInDay, offDutyStatuses, type IState } from "../helpers";
import { getWorkdayOffNeeded, shouldCarryWorkdayOffDuty } from "../hosCalculator";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

export type DailyOffConditions = {
    avail: number | undefined;
    offDutyNeeded: number | undefined;
    /** offDutyNeeded === 0: daily off-duty requirement is fully satisfied */
    requirementMet: boolean;
    /** current duty status is off-duty */
    isOffDuty: boolean;
    /** getDailyOffNeeded is returning the workday requirement: its carry branch applies and the workday requirement exceeds the daily remainder */
    workdayDominating: boolean;
    /** off-duty time is being credited toward the daily requirement (consecutiveOff >= restBreakMinimum) */
    credited: boolean;
};

export class DailyOffCalculator extends AvailabilityCalculator {
    private conditions(state: IState): DailyOffConditions {
        const avail = discreteAvailabilityFunctions.DailyOff(state, false);
        const offDutyNeeded = getDailyOffNeeded(state);
        
        const isOffDuty = offDutyStatuses.indexOf(state.current?.status) !== -1;
        const restBreakMinimum = state.config?.rest?.restBreakMinimum ?? 0;
        const workdayPathActive = shouldCarryWorkdayOffDuty(state);
        const workdayOffNeeded = workdayPathActive ? getWorkdayOffNeeded(state) : 0;
        // Mirrors getDailyOffNeeded exactly: the workday requirement is what it returns only while its
        // carry branch applies and the requirement strictly exceeds the daily remainder. On a tie the
        // remainder is what moves from the next instant on.
        const totalRequired = getTotalDailyOffDutyRequired(state.day, state.config) ?? 0;
        const remaining = getRemainingDailyOffDutyRequired(state.day, state.config) ?? 0;
        const carryApplies = workdayPathActive
            && (workdayOffNeeded > totalRequired || workdayOffNeeded > state.day.offDutyCount);

        return {
            avail,
            offDutyNeeded,
            requirementMet: (offDutyNeeded ?? 0) === 0,
            isOffDuty,
            workdayDominating: carryApplies && workdayOffNeeded > remaining,
            credited: state.day.consecutiveOff === undefined || restBreakMinimum === 0 || state.day.consecutiveOff >= restBreakMinimum,
        };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        // State          requirementMet  isOffDuty  workdayDominating  credited  → isConsuming
        // RequirementMetOnDuty     true      false          —             —          true
        // RequirementMetOffDuty    true       true          —             —          true
        // OnDuty                  false      false          —             —          true
        // WorkdayDominating       false       true          true          false      false
        // WorkdayDominating       false       true          true          true       true while rest allocation is open, else false
        // OffDutyUncredited       false       true         false         false       true
        // OffDutyCredited         false       true         false          true      false
        const c = this.conditions(state);
        if (c.requirementMet) return true;
        if (!c.isOffDuty) return true;
        // While countDailyRestBreaks is still allocating the break to daily rest it subtracts the
        // allocation from consecutiveOffCount, so the workday requirement holds still while the
        // day runs out; once the allocation is full the requirement falls with the clock.
        if (c.workdayDominating) return c.credited && this.dailyRestAllocationLeft(state) > 0;
        return !c.credited;
    }

    exhaustion(state: IState): Date | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined || c.offDutyNeeded === undefined) return undefined;

        const timeLeftInDay = getTimeLeftInDay(state);
        const violationDate = c.avail > 0 && c.avail < timeLeftInDay
            ? new Date(state.nextDate.getTime() + c.avail)
            : undefined;

        if (!c.requirementMet && !c.isOffDuty) return violationDate;                               // OnDuty
        if (!c.requirementMet && c.isOffDuty && !c.workdayDominating && !c.credited) return violationDate; // OffDutyUncredited

        // RequirementMetOnDuty natural path: avail = look-ahead formula spanning two days,
        // so avail > timeLeftInDay and violationDate is undefined above. The zero-crossing IS
        // real — it's the point of no return for tomorrow's requirement. Route it through
        // exhaustion() so the stability checker can verify it automatically.
        if (c.requirementMet && !c.isOffDuty && c.avail > 0) {
            return new Date(state.nextDate.getTime() + c.avail);
        }

        return undefined;
    }

    /**
     * How much more of the current break countDailyRestBreaks will allocate to today's daily rest,
     * or 0 when it is not allocating. On the evaluation's last segment it allocates
     * min(break, day window, remainingDailyRestRequired + deferralTime) as a temp restInfo and
     * subtracts that from consecutiveOffCount; break and window both grow 1:1, so the allocation
     * grows 1:1 until it meets the cap. The cap is fixed for the life of the break, which makes
     * nextDate + dailyRestAllocationLeft a stable date.
     */
    private dailyRestAllocationLeft(state: IState): number {
        const totalRest = getTotalDailyRestRequired(state.day, state.config);
        if (!state.config.rest?.restBreakMinimum || !state.day.restInfo || totalRest === undefined) {
            return 0;
        }
        const allocated = state.day.restInfo
            .filter(r => r.isTemp && !r.isWt)
            .reduce((sum, r) => sum + (r.toDate.getTime() - r.fromDate.getTime()), 0);
        const restBeforeBreak = (state.day.restCount ?? 0) - allocated;
        // Same arithmetic as countDailyRestBreaks, including its deferralTime addition.
        const cap = Math.max(0, Math.max(0, totalRest - restBeforeBreak) + (state.day.deferralTime ?? 0));
        return Math.max(0, cap - allocated);
    }

    /**
     * While the workday requirement governs, when it stops governing. Every input is fixed at a log
     * boundary except the three that move with the clock, and those move on known lines:
     *   - the workday requirement W falls at −1, except while rest allocation is open after the
     *     credit, when it holds still (dailyRestAllocationLeft);
     *   - day.offDutyCount D is flat until the credit and rises at +1 after it;
     *   - the daily remainder is total − D.
     * Uncredited, the requirement is released when W falls to the remainder (the value turns from
     * frozen to draining) or the carry branch in getDailyOffNeeded closes (the value steps down to
     * the remainder), whichever is first; the credit itself is a separate factor. Credited, W never
     * meets the remainder again (the gap only widens or holds), so release is the carry closing:
     * W ≤ total and W ≤ D, solved piecewise across the allocation window.
     */
    private workdayRequirementReleasedDate(state: IState, credited: boolean): Date | undefined {
        const W = getWorkdayOffNeeded(state);
        const total = getTotalDailyOffDutyRequired(state.day, state.config);
        const remaining = getRemainingDailyOffDutyRequired(state.day, state.config);
        if (total === undefined || remaining === undefined) {
            return undefined;
        }
        const D = state.day.offDutyCount;
        let offset: number;
        if (!credited) {
            offset = Math.min(W - remaining, Math.max(W - total, W - D));
        } else {
            const hold = this.dailyRestAllocationLeft(state);
            const untilWithinTotal = W <= total ? 0 : hold + (W - total);
            const untilWithinOffDuty = W - D <= hold ? Math.max(0, W - D) : Math.ceil((W + hold - D) / 2);
            offset = Math.max(untilWithinTotal, untilWithinOffDuty);
        }
        return new Date(state.nextDate.getTime() + offset);
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined || c.offDutyNeeded === undefined) return undefined;

        const nextDayBoundary = this.nextDayBoundary(state);

        // RequirementMetOnDuty natural path: zero-crossing handled by exhaustion(). No inflections.
        if (c.requirementMet && !c.isOffDuty) {
            return {};
        }

        // RequirementMetOffDuty
        if (c.requirementMet) {
            const restBreakMinimum = state.config?.rest?.restBreakMinimum ?? 0;
            const creditEventD1 = new Date(nextDayBoundary.getTime() + restBreakMinimum);
            return {
                creditEvent: { factor: () => creditEventD1, label: "credit threshold" },
            };
        }

        // OnDuty: zero-crossing handled by exhaustion(). nextDayBoundary is the genuine inflection.
        if (!c.isOffDuty) {
            return {
                nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
            };
        }

        const restBreakMinimum = state.config?.rest?.restBreakMinimum ?? 0;
        const breakStartTime = state.currentDate.getTime() - (state.day.consecutiveOff ?? 0);
        const creditEvent = new Date(breakStartTime + restBreakMinimum);

        // WorkdayDominating: the workday requirement governs the daily one until it is released or
        // the day ends. Until the break reaches restBreakMinimum the day has credited none of it; at
        // that instant allocateCounts credits the whole break in one step and countDailyRestBreaks
        // starts allocating it to daily rest, so both halves of getDailyOffNeeded move at once. That
        // credit is the first inflection of every break, including the log boundary that opens it
        // (day.consecutiveOff is 0 there). After it, the end of the rest allocation turns the value
        // from draining to frozen. The workday reset is not an inflection here: the carry always
        // closes first, because W falls to both total and D before it falls to zero.
        if (c.workdayDominating) {
            const releasedDate = this.workdayRequirementReleasedDate(state, c.credited);
            const allocationLeft = c.credited ? this.dailyRestAllocationLeft(state) : 0;
            const allocationEnds = allocationLeft > 0 ? new Date(state.nextDate.getTime() + allocationLeft) : undefined;
            return {
                nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
                workdayRequirementReleased: { factor: () => releasedDate, label: "workday requirement released" },
                ...(c.credited
                    ? { dailyRestAllocationEnds: { factor: () => allocationEnds, label: "rest allocation ends" } }
                    : { creditEvent: { factor: () => creditEvent, label: "credit threshold" } }),
            };
        }

        // OffDutyUncredited
        if (!c.credited) {
            // MYG-139401: this availability is exactly timeLeftInDay − offDutyNeeded and the
            // Emergency Response floor is timeLeftInDay, so while any requirement survives the
            // credit the floor governs either side of the threshold and the driver sees no
            // inflection there. Crossing it reduces offDutyNeeded by at most restBreakMinimum,
            // which is what makes "will it show through?" answerable rather than a guess.
            const availAtCredit = (c.avail ?? 0) + restBreakMinimum;
            if (isMaskedByEmergencyResponseFloor(state, creditEvent, availAtCredit)) {
                return {
                    nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
                };
            }
            return {
                nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
                creditEvent: { factor: () => creditEvent, label: "credit threshold" },
            };
        }

        // OffDutyCredited
        const requirementMetEvent = new Date(state.nextDate.getTime() + c.offDutyNeeded);
        return {
            nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
            requirementMet: { factor: () => requirementMetEvent, label: "requirement met" },
        };
    }
}

export const extractDailyOffConditions = (state: IState): DailyOffConditions => {
    return new DailyOffCalculator().extractConditions(state) as DailyOffConditions;
};

export const computeDailyOffResult = (state: IState): TComputedAvailabilityResult | undefined => {
    return new DailyOffCalculator().compute(state);
};
